"use client";

import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface PopupProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  zIndex?: number;
  className?: string;
  header?: React.ReactNode;
  initialHeight?: number; // percent of screen height (default 5)
  draggable?: boolean; // if false, disables drag handle and drag events
  onResize?: (newTranslateY: number) => void; // called when the popup is translated (moved up/down)
}

export const Popup: React.FC<PopupProps> = ({
  isOpen,
  onClose,
  children,
  header,
  className = "",
  initialHeight = 95,
  draggable = true,
  onResize,
  zIndex = 50,
}) => {
  const [translateY, setTranslateY] = useState(100); // 100% means fully hidden
  const popupRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [closing, setClosing] = useState(false);
  const [opening, setOpening] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startTranslate, setStartTranslate] = useState(0);
  const [lastY, setLastY] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);

  // Handle body scroll locking
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, opening, closing]);

  // Handle opening animation
  useEffect(() => {
    if (isOpen) {
      setOpening(true);
      setTranslateY(100 - initialHeight); // Use prop instead of hardcoded 5
      setClosing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen /* initialHeight */]);

  // Handle click on overlay
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.currentTarget === overlayRef.current) {
      handleClose();
    }
  };

  // Start closing animation
  const handleClose = () => {
    setClosing(true);
    setTranslateY(100); // Move down to fully hide
  };

  const handleTransitionEnd = (e: React.TransitionEvent) => {
    // Only handle transitions of the main popup div
    if (e.target === popupRef.current) {
      if (closing) {
        setClosing(false);
        onClose();
      } else if (opening) {
        setOpening(false);
      }
    }
  };

  // Handle drag start (only called from the handle)
  const handleDragStart = useCallback(
    (clientY: number) => {
      if (!popupRef.current) return;

      setIsDragging(true);
      setStartY(clientY);
      setLastY(clientY);
      setStartTranslate(translateY);
    },
    [translateY]
  );

  // Handle drag move
  const handleDragMove = useCallback(
    (clientY: number) => {
      if (!isDragging || !popupRef.current) return;

      setLastY(clientY);

      // Calculate new translateY value based on drag distance
      const deltaY = clientY - startY;
      const deltaDVH = (deltaY / window.innerHeight) * 100;
      let newTranslateY = startTranslate + deltaDVH;

      // Bound the translation to minimum 5%
      newTranslateY = Math.max(newTranslateY, 5);

      // Check if dragging down should trigger close
      if (newTranslateY > 45) {
        handleClose();
        setIsDragging(false);
        return;
      }

      setTranslateY(newTranslateY);
      onResize?.(100 - newTranslateY);
    },
    [isDragging, startY, startTranslate, onResize]
  );

  // Handle drag end
  const handleDragEnd = useCallback(
    (e: TouchEvent | MouseEvent) => {
      if (isDragging) {
        setIsDragging(false);

        // Determine drag direction (negative means dragging up)
        const dragDirection = lastY - startY;
        let newTranslateY = translateY;

        // If dragging up, snap to 5, if dragging down, snap to 45
        if (dragDirection < 0) {
          newTranslateY = 5;
          setTranslateY(5);
        } else if (dragDirection > 0) {
          newTranslateY = 45;
          setTranslateY(45);
        }

        onResize?.(100 - newTranslateY);
      } else if (
        translateY > 5 &&
        (e as TouchEvent).changedTouches?.[0]?.clientY < window.innerHeight / 2
      ) {
        setTranslateY(5);
        onResize?.(95);
      }
    },
    [isDragging, lastY, startY, translateY, onResize]
  );

  // Event handlers for the drag handle
  const handleMouseDown = (e: React.MouseEvent) => {
    handleDragStart(e.clientY);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging) {
      handleDragMove(e.touches[0].clientY);
    }
  };

  // Add/remove mouse move and up handlers globally
  useEffect(() => {
    if (!draggable) return;
    if (isDragging) {
      const mouseMoveHandler = (e: MouseEvent) => {
        handleDragMove(e.clientY);
      };
      const mouseUpHandler = (e: MouseEvent) => handleDragEnd(e);

      document.addEventListener("mousemove", mouseMoveHandler);
      document.addEventListener("mouseup", mouseUpHandler);

      // Also listen for touchmove/touchend globally when dragging
      const touchMoveHandler = (e: TouchEvent) => {
        if (e.touches.length === 1) {
          handleDragMove(e.touches[0].clientY);
        }
      };
      const touchEndHandler = (e: TouchEvent) => handleDragEnd(e);

      document.addEventListener("touchmove", touchMoveHandler, { passive: false });
      document.addEventListener("touchend", touchEndHandler);

      return () => {
        document.removeEventListener("mousemove", mouseMoveHandler);
        document.removeEventListener("mouseup", mouseUpHandler);
        document.removeEventListener("touchmove", touchMoveHandler);
        document.removeEventListener("touchend", touchEndHandler);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd, draggable]);

  // Special handler for content area top events
  useEffect(() => {
    if (!draggable) return;
    if (!contentRef.current || !isOpen) return;

    const content = contentRef.current;

    // Handle top-of-content swipe down to drag
    const handleContentTouchStart = (e: TouchEvent) => {
      if (translateY > 5) return; // only for fully expanded popups
      if (content.scrollTop > 0) return; // only at the top of content

      const touch = e.touches[0];
      const startY = touch.clientY;
      let startedDrag = false;

      const moveHandler = (moveEvent: TouchEvent) => {
        if (startedDrag) return;

        const moveTouch = moveEvent.touches[0];
        const deltaY = moveTouch.clientY - startY;

        // If swiping down at the top of content, start dragging
        if (deltaY > 10 && content.scrollTop <= 0) {
          startedDrag = true;
          handleDragStart(moveTouch.clientY);
          // The global isDragging handlers will take over from here
        }
      };

      const endHandler = () => {
        document.removeEventListener("touchmove", moveHandler);
        document.removeEventListener("touchend", endHandler);
      };

      document.addEventListener("touchmove", moveHandler, { passive: true });
      document.addEventListener("touchend", endHandler);
    };

    content.addEventListener("touchstart", handleContentTouchStart, { passive: true });

    return () => {
      content.removeEventListener("touchstart", handleContentTouchStart);
    };
  }, [isOpen, translateY, handleDragStart, draggable]);

  const portalTarget =
    typeof window !== "undefined" ? document.getElementById("popup-portal") : null;
  return portalTarget
    ? createPortal(
        <div className={isOpen ? "" : "opacity-0 pointer-events-none"}>
          <div
            ref={overlayRef}
            className="fixed inset-0 transition-background-color duration-250 touch-none"
            style={{
              backgroundColor: `rgba(0, 0, 0, ${Math.min(0.5, (70 - translateY) / 100)}`,
              zIndex: zIndex - 1,
            }}
            onClick={handleOverlayClick}
          ></div>
          <div
            ref={popupRef}
            onTransitionEnd={handleTransitionEnd}
            className={`fixed w-full left-1/2 -translate-x-1/2 bottom-0
              text-gray-900 dark:text-white shadow-[0_-15px_30px_-8px_rgba(0,0,0,0.2)] dark:shadow-[0_-15px_30px_-8px_rgba(0,0,0,0.8)] rounded-t-4xl h-[95dvh] ${
                isDragging ? "" : "transition-transform duration-250"
              } ${isDragging ? "select-none" : ""} ${className}`}
            style={{
              transform: `translateY(${translateY}dvh)`,
              display: "flex",
              flexDirection: "column",
              zIndex: zIndex + 1,
            }}
            onTouchEnd={draggable ? (e) => handleDragEnd(e as unknown as TouchEvent) : undefined}
          >
            {/* Drag handle - only this area initiates dragging */}
            {draggable ? (
              <div
                ref={handleRef}
                className="flex justify-center pt-1.5 pb-4 sticky top-0 z-10 cursor-grab active:cursor-grabbing touch-none"
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={(e) => handleDragEnd(e as unknown as TouchEvent)}
              >
                <div className={`h-1 w-10 bg-black/10 dark:bg-white/25 rounded-full`}></div>
              </div>
            ) : (
              <div className="h-2"></div>
            )}
            {header && (
              <div
                className="top-0 z-10 w-full max-w-md mx-auto px-6"
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={(e) => handleDragEnd(e as unknown as TouchEvent)}
              >
                {header}
                <div className="border-b border-black/10 dark:border-white/15 mt-4 mx-8 "></div>
              </div>
            )}
            {/* Content container - scrollable area */}
            <div
              className="overflow-y-auto flex-grow overscroll-none pt-4 pb-8 px-2"
              id="popup-content"
              ref={contentRef}
              onScroll={(e) => {
                if (translateY <= 5) return;
                const target = e.target as HTMLElement & { lastScrollTop?: number };
                if (target.scrollTop > (target.lastScrollTop ?? 0)) {
                  setTranslateY(5);
                }
                target.lastScrollTop = target.scrollTop;
              }}
            >
              {children}
            </div>
          </div>
        </div>,
        portalTarget
      )
    : null;
};
